import { useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { z } from "zod";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { supabase } from "@/integrations/supabase/client";
import { trackCTA, trackConversion } from "@/lib/tracking";

const SERVICE_OPTIONS = [
  "Foundation FSAP retainer",
  "Priority FSAP retainer",
  "Elite FSAP retainer",
  "Compassion Plan",
  "Strategy Session (à la carte)",
  "ARD/IEP Meeting Representation",
  "Compliance / Records Review",
  "Advocacy Presence & Support",
  "Emergency / After-Hours Support",
];
const DOCUMENT_OPTIONS = ["IEP", "504 Plan", "FIE / Evaluation", "Meeting notices", "Medical records", "Guardianship / POA"];
const FINANCIAL_OPTIONS = ["Sliding scale", "Scholarship", "Fee waiver"];

const schema = z.object({
  client_name: z.string().trim().min(1, "Required").max(200),
  client_dob: z.string().optional(),
  guardian_name: z.string().max(200).optional(),
  guardian_relation: z.string().max(100).optional(),
  email: z.string().trim().email("Valid email required").max(255),
  phone: z.string().max(30).optional(),
  city_state: z.string().max(120).optional(),
  best_contact_time: z.string().max(120).optional(),
  requested_services: z.array(z.string()).min(1, "Select at least one service"),
  urgency: z.string().max(80).optional(),
  upcoming_deadlines: z.string().max(500).optional(),
  background_summary: z.string().max(2000).optional(),
  disability_context: z.string().max(1000).optional(),
  institutions: z.string().max(500).optional(),
  key_concerns: z.string().max(1000).optional(),
  documents_available: z.array(z.string()),
  risk_transportation: z.string().max(500).optional(),
  risk_behavior: z.string().max(500).optional(),
  risk_medical: z.string().max(500).optional(),
  income_context: z.string().max(500).optional(),
  hardship_description: z.string().max(1500).optional(),
  has_caseworker: z.boolean(),
  caseworker_contact: z.string().max(300).optional(),
  seeking_financial_help: z.array(z.string()),
  consent_advocacy_only: z.literal(true, { message: "Consent required" }),
});

const AdvocacyIntake = () => {
  const [params] = useSearchParams();
  const preselectedTier = params.get("tier");
  const financialFocus = params.get("financial") === "review";

  const initialServices = useMemo(() => {
    const map: Record<string, string> = {
      foundation: "Foundation FSAP retainer",
      priority: "Priority FSAP retainer",
      elite: "Elite FSAP retainer",
      compassion: "Compassion Plan",
    };
    return preselectedTier && map[preselectedTier] ? [map[preselectedTier]] : [];
  }, [preselectedTier]);

  const [form, setForm] = useState({
    client_name: "", client_dob: "", guardian_name: "", guardian_relation: "",
    email: "", phone: "", city_state: "", best_contact_time: "",
    requested_services: initialServices,
    urgency: "", upcoming_deadlines: "", background_summary: "", disability_context: "",
    institutions: "", key_concerns: "",
    documents_available: [] as string[],
    risk_transportation: "", risk_behavior: "", risk_medical: "",
    income_context: "", hardship_description: "",
    has_caseworker: false, caseworker_contact: "",
    seeking_financial_help: financialFocus ? ["Sliding scale"] : ([] as string[]),
    consent_advocacy_only: false,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const toggle = (field: "requested_services" | "documents_available" | "seeking_financial_help", value: string) => {
    setForm((f) => {
      const has = f[field].includes(value);
      return { ...f, [field]: has ? f[field].filter((v) => v !== value) : [...f[field], value] };
    });
  };

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const iss of parsed.error.issues) fe[iss.path[0] as string] = iss.message;
      setErrors(fe);
      trackCTA("fsap_intake_validation_error", { count: Object.keys(fe).length });
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const { error } = await supabase.from("fsap_intakes").insert({
        ...parsed.data,
        consent_advocacy_only: true,
        client_dob: parsed.data.client_dob || null,
        institutions: parsed.data.institutions
          ? parsed.data.institutions.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
        source: "advocacy_intake_page",
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 300) : null,
      });
      if (error) throw error;
      trackConversion("fsap_intake_submit", {
        services: parsed.data.requested_services.join(","),
        seeking_financial_help: parsed.data.seeking_financial_help.join(",") || "none",
      });
      setSuccess(true);
    } catch (err) {
      setErrors({ _root: err instanceof Error ? err.message : "Submission failed" });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-bg-primary border border-fg-muted/25 px-4 py-3 text-sm focus:border-gold outline-none";
  const labelCls = "block text-xs uppercase tracking-[0.14em] text-fg-muted mb-2";

  if (success) {
    return (
      <div className="min-h-screen bg-bg-primary text-fg-primary">
        <PageMeta title="Intake received — FSAP" description="Your Full-Scope Advocacy Partner intake has been received. Dr. Sully's team reviews every request and responds with next steps." path="/advocacy/intake" noindex />
        <SiteNav />
        <main className="max-w-2xl mx-auto px-6 py-32 text-center">
          <CheckCircle2 className="h-16 w-16 text-gold mx-auto mb-6" />
          <h1 className="font-display text-4xl mb-4">Intake received.</h1>
          <p className="text-fg-secondary mb-8">
            The FSAP Care Team will review your submission and follow up within 2 business days. If you selected
            sliding scale, scholarship, or fee waiver, financial review typically adds 3–5 business days.
          </p>
          <Link to="/advocacy" className="inline-flex items-center gap-2 border border-gold/50 px-6 py-3 font-display uppercase tracking-[0.18em] text-xs">
            Back to Advocacy <ArrowRight className="h-4 w-4" />
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary text-fg-primary">
      <PageMeta
        title="FSAP Intake — Advocacy Request Form | Dr. Sully"
        description="Submit your Full-Scope Advocacy Partner intake: client details, requested services, urgency, background, documents, risk factors, and financial hardship review."
        path="/advocacy/intake"
      />
      <SiteNav />
      <main>
        <section className="border-b border-fg-muted/10 bg-bg-secondary">
          <div className="max-w-3xl mx-auto px-6 py-16">
            <p className="font-display uppercase tracking-[0.24em] text-gold text-xs mb-3">FSAP Intake</p>
            <h1 className="font-display text-4xl md:text-5xl leading-tight">Tell us what you're navigating.</h1>
            <p className="text-fg-secondary mt-4">
              This is a support-and-advocacy intake, not legal or medical advice. Complete what you can — you can add details on the discovery call.
            </p>
          </div>
        </section>

        <form onSubmit={submit} className="max-w-3xl mx-auto px-6 py-12 space-y-10">
          {/* Client */}
          <fieldset className="space-y-4">
            <legend className="font-display text-xl mb-2">Client & guardian</legend>
            <div>
              <label className={labelCls}>Client full name *</label>
              <input className={inputCls} value={form.client_name} onChange={(e) => set("client_name", e.target.value)} />
              {errors.client_name && <p className="text-red-400 text-xs mt-1">{errors.client_name}</p>}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Client date of birth</label>
                <input type="date" className={inputCls} value={form.client_dob} onChange={(e) => set("client_dob", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>City, State</label>
                <input className={inputCls} value={form.city_state} onChange={(e) => set("city_state", e.target.value)} placeholder="San Antonio, TX" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Guardian / parent name</label>
                <input className={inputCls} value={form.guardian_name} onChange={(e) => set("guardian_name", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Relationship to client</label>
                <input className={inputCls} value={form.guardian_relation} onChange={(e) => set("guardian_relation", e.target.value)} />
              </div>
            </div>
          </fieldset>

          {/* Contact */}
          <fieldset className="space-y-4">
            <legend className="font-display text-xl mb-2">Contact</legend>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Email *</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>
              <div>
                <label className={labelCls}>Phone</label>
                <input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
              </div>
            </div>
            <div>
              <label className={labelCls}>Best time to contact</label>
              <input className={inputCls} value={form.best_contact_time} onChange={(e) => set("best_contact_time", e.target.value)} />
            </div>
          </fieldset>

          {/* Services */}
          <fieldset className="space-y-4">
            <legend className="font-display text-xl mb-2">Requested services *</legend>
            <div className="grid md:grid-cols-2 gap-2">
              {SERVICE_OPTIONS.map((s) => (
                <label key={s} className="flex items-start gap-2 text-sm border border-fg-muted/15 p-3 cursor-pointer hover:border-gold/40">
                  <input type="checkbox" checked={form.requested_services.includes(s)} onChange={() => toggle("requested_services", s)} className="mt-0.5" />
                  <span>{s}</span>
                </label>
              ))}
            </div>
            {errors.requested_services && <p className="text-red-400 text-xs">{errors.requested_services}</p>}
          </fieldset>

          {/* Urgency */}
          <fieldset className="space-y-4">
            <legend className="font-display text-xl mb-2">Urgency & deadlines</legend>
            <div>
              <label className={labelCls}>Urgency</label>
              <select className={inputCls} value={form.urgency} onChange={(e) => set("urgency", e.target.value)}>
                <option value="">Select…</option>
                <option>Immediate (within 7 days)</option>
                <option>This month</option>
                <option>Next 60 days</option>
                <option>Planning ahead</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Upcoming deadlines (ARD dates, hearings, evaluations)</label>
              <textarea rows={3} className={inputCls} value={form.upcoming_deadlines} onChange={(e) => set("upcoming_deadlines", e.target.value)} />
            </div>
          </fieldset>

          {/* Background */}
          <fieldset className="space-y-4">
            <legend className="font-display text-xl mb-2">Background</legend>
            <div>
              <label className={labelCls}>Background summary</label>
              <textarea rows={4} className={inputCls} value={form.background_summary} onChange={(e) => set("background_summary", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Disability / diagnosis context</label>
              <textarea rows={3} className={inputCls} value={form.disability_context} onChange={(e) => set("disability_context", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Institutions involved (comma-separated: school, agency, provider)</label>
              <input className={inputCls} value={form.institutions} onChange={(e) => set("institutions", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Key concerns</label>
              <textarea rows={3} className={inputCls} value={form.key_concerns} onChange={(e) => set("key_concerns", e.target.value)} />
            </div>
          </fieldset>

          {/* Documents */}
          <fieldset className="space-y-4">
            <legend className="font-display text-xl mb-2">Documents available</legend>
            <div className="grid md:grid-cols-3 gap-2">
              {DOCUMENT_OPTIONS.map((d) => (
                <label key={d} className="flex items-center gap-2 text-sm border border-fg-muted/15 p-3 cursor-pointer">
                  <input type="checkbox" checked={form.documents_available.includes(d)} onChange={() => toggle("documents_available", d)} />
                  <span>{d}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Risk */}
          <fieldset className="space-y-4">
            <legend className="font-display text-xl mb-2">Risk & safety notes</legend>
            <div>
              <label className={labelCls}>Transportation needs / limits</label>
              <input className={inputCls} value={form.risk_transportation} onChange={(e) => set("risk_transportation", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Behavior / de-escalation notes</label>
              <input className={inputCls} value={form.risk_behavior} onChange={(e) => set("risk_behavior", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Medical / mobility notes</label>
              <input className={inputCls} value={form.risk_medical} onChange={(e) => set("risk_medical", e.target.value)} />
            </div>
          </fieldset>

          {/* Financial */}
          <fieldset className="space-y-4">
            <legend className="font-display text-xl mb-2">Financial context</legend>
            <div>
              <label className={labelCls}>Income / benefits context</label>
              <input className={inputCls} value={form.income_context} onChange={(e) => set("income_context", e.target.value)} placeholder="e.g. SSI, SSDI, household income range" />
            </div>
            <div>
              <label className={labelCls}>Financial hardship description</label>
              <textarea rows={3} className={inputCls} value={form.hardship_description} onChange={(e) => set("hardship_description", e.target.value)} />
            </div>
            <div className="grid md:grid-cols-3 gap-2">
              {FINANCIAL_OPTIONS.map((f) => (
                <label key={f} className="flex items-center gap-2 text-sm border border-fg-muted/15 p-3 cursor-pointer">
                  <input type="checkbox" checked={form.seeking_financial_help.includes(f)} onChange={() => toggle("seeking_financial_help", f)} />
                  <span>Seeking: {f}</span>
                </label>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm mt-4">
              <input type="checkbox" checked={form.has_caseworker} onChange={(e) => set("has_caseworker", e.target.checked)} />
              I have a caseworker, social worker, or support coordinator
            </label>
            {form.has_caseworker && (
              <div>
                <label className={labelCls}>Caseworker contact (name, agency, phone/email)</label>
                <input className={inputCls} value={form.caseworker_contact} onChange={(e) => set("caseworker_contact", e.target.value)} />
              </div>
            )}
          </fieldset>

          {/* Consent */}
          <fieldset className="space-y-4 border-t border-fg-muted/15 pt-8">
            <label className="flex items-start gap-3 text-sm">
              <input type="checkbox" checked={form.consent_advocacy_only} onChange={(e) => set("consent_advocacy_only", e.target.checked)} className="mt-1" />
              <span>
                I understand FSAP provides <strong>advocacy and support only</strong> — not legal advice, not medical advice, and not therapy. There are no outcome guarantees. Sliding scale, scholarships, and fee waivers are case-by-case and subject to approval.
              </span>
            </label>
            {errors.consent_advocacy_only && <p className="text-red-400 text-xs">{errors.consent_advocacy_only}</p>}
          </fieldset>

          {errors._root && <p className="text-red-400 text-sm">{errors._root}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.18em] text-xs px-8 py-4 hover:brightness-110 disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit intake"}
            <ArrowRight className="h-4 w-4" />
          </button>
        </form>
      </main>
      <SiteFooter />
    </div>
  );
};

export default AdvocacyIntake;
